import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  MessageSquare, 
  Clock,
  Sparkles
} from "lucide-react";

interface QuickActionsPanelProps {
  deal: any;
  onAction: (action: "approve" | "reject" | "request_info" | "flag_review", reason?: string) => void;
  compact?: boolean;
}

interface SuggestedAction {
  action: "approve" | "reject" | "request_info" | "flag_review";
  confidence: "high" | "medium" | "low";
  reasoning: string;
  icon: any;
  variant: "default" | "destructive" | "outline" | "secondary";
  label: string;
}

const QuickActionsPanel = ({ deal, onAction, compact = false }: QuickActionsPanelProps) => {
  const analyzeDeal = (): SuggestedAction[] => {
    const actions: SuggestedAction[] = [];
    const aiRiskScore = deal.ai_risk_score || 0;
    const aiRiskLevel = deal.ai_risk_level;
    const concerns = deal.ai_concerns || [];
    const recommendations = deal.ai_recommendations || [];
    const amount = deal.amount || 0;

    // High confidence approval - low risk, no concerns
    if (aiRiskScore <= 20 && concerns.length === 0 && amount < 5000) {
      actions.push({
        action: "approve",
        confidence: "high",
        reasoning: "Low risk score, no compliance concerns, standard deal value",
        icon: CheckCircle,
        variant: "default",
        label: "Auto-Approve"
      });
    }
    
    // Medium confidence approval - moderate risk but manageable
    else if (aiRiskScore <= 40 && concerns.length <= 1 && aiRiskLevel !== "high") {
      actions.push({
        action: "approve",
        confidence: "medium",
        reasoning: "Moderate risk with minor concerns that appear manageable",
        icon: CheckCircle,
        variant: "outline",
        label: "Approve with Conditions"
      });
    }

    // High confidence rejection - prohibited categories or critical violations
    if (aiRiskScore >= 80 || aiRiskLevel === "critical" || 
        concerns.some((c: any) => c.severity === "critical" || c.type === "prohibited_category")) {
      actions.push({
        action: "reject",
        confidence: "high",
        reasoning: "Critical violations or prohibited category detected",
        icon: XCircle,
        variant: "destructive",
        label: "Reject Deal"
      });
    }

    // Request more information - missing data or unclear terms
    if (
      !deal.description || 
      !deal.deliverables || 
      (concerns.length > 0 && aiRiskScore < 80) ||
      recommendations.some((r: any) => r.type === "clarification_needed")
    ) {
      actions.push({
        action: "request_info",
        confidence: "medium",
        reasoning: "Missing details or requires clarification on terms",
        icon: MessageSquare,
        variant: "outline",
        label: "Request Information"
      });
    }

    // Flag for manual review - high value or borderline cases
    if (
      (amount >= 10000 || (aiRiskScore >= 50 && aiRiskScore < 80)) &&
      !actions.some(a => a.action === "reject")
    ) {
      actions.push({
        action: "flag_review",
        confidence: "high",
        reasoning: amount >= 10000 
          ? "High-value deal requires senior compliance review" 
          : "Borderline risk score warrants additional scrutiny",
        icon: AlertTriangle,
        variant: "secondary",
        label: "Flag for Senior Review"
      });
    }

    // Default fallback - manual review if no clear action
    if (actions.length === 0) {
      actions.push({
        action: "flag_review",
        confidence: "medium",
        reasoning: "No automatic recommendation - requires manual assessment",
        icon: Clock,
        variant: "outline",
        label: "Manual Review"
      });
    }

    return actions;
  };

  const suggestedActions = analyzeDeal();
  const primaryAction = suggestedActions[0];

  const getConfidenceBadge = (confidence: string) => {
    const variants: Record<string, any> = {
      high: "default",
      medium: "secondary",
      low: "outline"
    };
    return (
      <Badge variant={variants[confidence]} className="text-xs">
        {confidence === "high" ? "95%" : confidence === "medium" ? "75%" : "50%"} confidence
      </Badge>
    );
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg border border-border">
        <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate">{primaryAction.label}</p>
          <p className="text-xs text-muted-foreground truncate">{primaryAction.reasoning}</p>
        </div>
        <Button
          size="sm"
          variant={primaryAction.variant}
          onClick={() => onAction(primaryAction.action)}
          className="flex-shrink-0"
        >
          <primaryAction.icon className="h-3 w-3 mr-1" />
          {primaryAction.action === "approve" ? "Approve" : 
           primaryAction.action === "reject" ? "Reject" :
           primaryAction.action === "request_info" ? "Request Info" : "Flag"}
        </Button>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Suggested Actions
          </CardTitle>
          {getConfidenceBadge(primaryAction.confidence)}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {suggestedActions.map((action, index) => (
          <div 
            key={index}
            className={`p-4 rounded-lg border ${
              index === 0 ? "bg-primary/5 border-primary/20" : "bg-muted/30 border-border"
            }`}
          >
            <div className="flex items-start gap-3">
              <action.icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                index === 0 ? "text-primary" : "text-muted-foreground"
              }`} />
              <div className="flex-1 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold">{action.label}</p>
                  {index === 0 && (
                    <Badge variant="outline" className="text-xs">
                      Recommended
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{action.reasoning}</p>
                <Button
                  size="sm"
                  variant={action.variant}
                  onClick={() => onAction(action.action)}
                  className="w-full mt-2"
                >
                  <action.icon className="h-4 w-4 mr-2" />
                  {action.action === "approve" ? "Approve Deal" : 
                   action.action === "reject" ? "Reject Deal" :
                   action.action === "request_info" ? "Request More Information" : 
                   "Flag for Senior Review"}
                </Button>
              </div>
            </div>
          </div>
        ))}

        {deal.ai_analyzed_at && (
          <div className="pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground">
              AI analysis completed {new Date(deal.ai_analyzed_at).toLocaleDateString()} at{" "}
              {new Date(deal.ai_analyzed_at).toLocaleTimeString()}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuickActionsPanel;
